// A deterministic game model, shared by the canvas controller and gameplay tests.

const CUP_OPENING_HALF_WIDTH = 47;
const CATCH_DURATION = .26;

export function catchPose(game, effect) {
  const progress = Math.min(1, effect.age / CATCH_DURATION);
  return { x: game.cupX + effect.cupOffset * (1 - progress * .25), y: game.height - 100 + progress * 30, angle: effect.angle, visible: progress < 1 };
}

export function createGame(height = 467) {
  return { width: 600, height, cupX: 300, cupWidth: 108, score: 0, lives: 3, elapsed: 0, level: 0, spawnIn: .7, items: [], state: 'idle' };
}
export function moveCup(game, x) {
  game.cupX = Math.max(game.cupWidth / 2, Math.min(game.width - game.cupWidth / 2, x));
}
export function resizeGame(game, height) {
  const ratio = height / game.height;
  for (const item of game.items) { item.y *= ratio; item.speed *= ratio; }
  game.height = height;
}
export function advanceGame(game, seconds, direction = 0, random = Math.random) {
  if (game.state !== 'playing') return [];
  const dt = Math.min(.05, Math.max(0, seconds));
  const events = [];
  game.elapsed += dt;
  const level = Math.min(10, Math.floor(game.elapsed / 30));
  if (level > game.level) events.push({ type: 'level' });
  game.level = level;
  moveCup(game, game.cupX + direction * 470 * dt);
  game.spawnIn -= dt;
  if (game.spawnIn <= 0) {
    const roll = random();
    const type = roll < Math.min(.42, .2 + game.score * .0016) ? 'decaf' : roll > .93 ? 'gold' : 'coffee';
    game.items.push({ x: 18 + random() * 564, y: -24, type, angle: (random() - .5) * 1.5,
      speed: (game.height * .3 + Math.min(game.score, 240) * game.height * .0016) * (1 + level * .2) * (.85 + random() * .3) });
    game.spawnIn = Math.max(.36, .92 - game.score * .004);
  }
  const rimY = game.height - 100;
  for (let i = game.items.length - 1; i >= 0; i--) {
    const item = game.items[i];
    const previousY = item.y;
    item.y += item.speed * dt;
    // Catch at the opening, never through the side or bottom of the mug.
    if (previousY <= rimY && item.y >= rimY && Math.abs(item.x - game.cupX) <= CUP_OPENING_HALF_WIDTH) {
      game.items.splice(i, 1);
      if (item.type === 'decaf') game.lives--;
      else game.score += item.type === 'gold' ? 5 : 1;
      events.push({ type: item.type, x: item.x, y: rimY, cupOffset: item.x - game.cupX, angle: item.angle });
      if (game.lives === 0) { game.state = 'over'; events.push({ type: 'over' }); break; }
    } else if (item.y > game.height + 24) game.items.splice(i, 1);
  }
  return events;
}

if (typeof window !== 'undefined' && !customElements.get('bean-catcher')) {
  customElements.define('bean-catcher', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const listen = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal: this.controller.signal });
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) { this.querySelector('[data-message]').textContent = 'Your browser does not support this game. Please try another browser.'; this.querySelector('[data-play]').disabled = true; return; }
      this.game = createGame();
      this.effects = [];
      this.keys = new Set();
      this.logo = new Image();
      listen(this.logo, 'load', () => this.draw());
      if (this.dataset.logoSrc) this.logo.src = this.dataset.logoSrc;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.best = 0;
      try { this.best = Math.max(0, Number(localStorage.getItem('chefman-bean-catcher-hi')) || 0); } catch { /* Storage can be disabled. */ }
      this.querySelector('[data-best]').textContent = this.best;
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas);
      listen(this.querySelector('[data-play]'), 'click', () => this.game.state === 'paused' ? this.resume() : this.start());
      listen(this.querySelector('[data-pause]'), 'click', () => this.pause());
      listen(this.canvas, 'pointerdown', event => {
        if (this.game.state !== 'playing') return;
        this.canvas.focus({ preventScroll: true });
        this.canvas.setPointerCapture(event.pointerId);
        this.pointer(event);
      });
      listen(this.canvas, 'pointermove', event => {
        if (event.pointerType === 'mouse' || this.canvas.hasPointerCapture(event.pointerId)) this.pointer(event);
      });
      listen(this.canvas, 'keydown', event => {
        const key = event.key.toLowerCase();
        if (['arrowleft', 'arrowright', 'a', 'd'].includes(key)) { event.preventDefault(); this.keys.add(key); }
        if ((key === 'p' || key === 'escape') && !event.repeat) { event.preventDefault(); this.pause(); }
      });
      listen(window, 'keyup', event => this.keys.delete(event.key.toLowerCase()));
      listen(this.canvas, 'blur', () => { this.keys.clear(); if (this.game.state === 'playing') this.pause(); });
      listen(window, 'blur', () => this.pause());
      listen(document, 'visibilitychange', () => { if (document.hidden) this.pause(); });
      this.querySelector('[data-message]').textContent = 'Catch coffee (+1) and gold (+5). Dodge the red decaf—three hits end your run.';
      this.resize();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.toastTimer);
      this.controller = null;
    }
    resize() {
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      resizeGame(this.game, 600 * rect.height / rect.width);
      this.draw();
    }
    pointer(event) {
      if (this.game.state !== 'playing') return;
      const rect = this.canvas.getBoundingClientRect();
      moveCup(this.game, (event.clientX - rect.left) / rect.width * 600);
    }
    start() {
      this.game = createGame(this.game.height);
      this.effects = [];
      this.keys.clear();
      this.querySelector('[data-toast]').classList.remove('is-visible');
      this.hud();
      this.resume();
    }
    resume() {
      this.game.state = 'playing';
      this.canvas.tabIndex = 0;
      this.querySelector('[data-overlay]').hidden = true;
      this.querySelector('[data-pause]').disabled = false;
      this.canvas.focus({ preventScroll: true });
      this.querySelector('[data-status]').textContent = `Game playing. ${this.game.score} points. ${this.game.lives} lives remaining.`;
      this.lastTime = performance.now();
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    pause() {
      if (this.game.state !== 'playing') return;
      this.game.state = 'paused';
      this.keys.clear();
      cancelAnimationFrame(this.frame);
      this.overlay('TAKE YOUR TIME', 'Coffee can wait.', 'Your score is safe. Pick up where you left off.', 'Keep brewing');
      this.querySelector('[data-status]').textContent = 'Game paused.';
    }
    overlay(kicker, title, message, button) {
      this.canvas.tabIndex = -1;
      this.querySelector('[data-kicker]').textContent = kicker;
      this.querySelector('[data-title]').textContent = title;
      this.querySelector('[data-message]').textContent = message;
      this.querySelector('[data-play]').firstChild.textContent = button + ' ';
      this.querySelector('[data-overlay]').hidden = false;
      this.querySelector('[data-pause]').disabled = true;
      // Do not pull keyboard focus back from navigation or another browser tab.
      if (this.contains(document.activeElement)) this.querySelector('[data-play]').focus({ preventScroll: true });
    }
    over() {
      const record = this.game.score > this.best;
      this.best = Math.max(this.best, this.game.score);
      try { localStorage.setItem('chefman-bean-catcher-hi', String(this.best)); } catch { /* Session still works without storage. */ }
      this.querySelector('[data-best]').textContent = this.best;
      this.overlay(record ? 'A NEW PERSONAL BEST' : 'THAT WAS A GOOD BREAK', `${this.game.score} points. Nicely brewed.`, `Your best is ${this.best}. There’s always room for another cup.`, 'Brew another');
      this.querySelector('[data-status]').textContent = `Game over. ${this.game.score} points. Personal best ${this.best}.`;
    }
    hud() {
      this.querySelector('[data-score]').textContent = this.game.score;
      const lives = this.querySelector('[data-lives]');
      lives.textContent = Array.from({ length: 3 }, (_, i) => i < this.game.lives ? '●' : '○').join(' ');
      lives.setAttribute('aria-label', `${this.game.lives} lives remaining`);
    }
    toast(text) {
      const toast = this.querySelector('[data-toast]');
      toast.textContent = text;
      toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1600);
    }
    tick(time) {
      if (this.game.state !== 'playing') return;
      const dt = Math.min(.05, (time - this.lastTime) / 1000);
      this.lastTime = time;
      const direction = Number(this.keys.has('arrowright') || this.keys.has('d')) - Number(this.keys.has('arrowleft') || this.keys.has('a'));
      for (const event of advanceGame(this.game, dt, direction)) {
        if (event.type === 'level') this.toast('Picking up the pace');
        else if (event.type !== 'over') {
          this.effects.push({ ...event, age: 0 });
          this.hud();
          if (event.type === 'decaf') { this.toast('Decaf! Keep an eye on the red beans.'); this.querySelector('[data-status]').textContent = `${this.game.lives} lives remaining.`; }
        }
      }
      this.effects = this.effects.filter(effect => (effect.age += dt) < .7);
      this.draw();
      if (this.game.state === 'over') this.over();
      else this.frame = requestAnimationFrame(next => this.tick(next));
    }
    draw() {
      const c = this.ctx, { height: h, cupX: x } = this.game;
      const scale = this.canvas.width / 600;
      c.setTransform(scale, 0, 0, scale, 0, 0);
      c.clearRect(0, 0, 600, h);
      const wall = c.createLinearGradient(0, 0, 600, h);
      wall.addColorStop(0, '#eff1ef'); wall.addColorStop(1, '#dce1e2');
      c.fillStyle = wall; c.fillRect(0, 0, 600, h);
      // Architectural window, soft morning light, and tiled café wall.
      c.save(); c.beginPath(); c.roundRect(143, 34, 314, h - 101, [155, 155, 0, 0]); c.clip();
      c.fillStyle = '#d9e1e2'; c.fillRect(143, 34, 314, h);
      const light = c.createLinearGradient(143, 34, 457, h);
      light.addColorStop(0, '#e9edef'); light.addColorStop(1, '#afbec6');
      c.fillStyle = light; c.fillRect(153, 44, 294, h);
      c.fillStyle = '#f7f8f5'; c.beginPath(); c.arc(352, 126, 47, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#bcc9ce'; c.beginPath(); c.ellipse(218, h - 92, 180, 140, -.3, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#8e9fa9'; c.beginPath(); c.ellipse(443, h - 71, 190, 95, .1, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#f4f5f2'; c.fillRect(296, 30, 8, h); c.fillRect(140, 211, 330, 8); c.restore();
      c.strokeStyle = '#c6ced1'; c.lineWidth = 1;
      for (let y = h - 150; y < h - 50; y += 35) { c.beginPath(); c.moveTo(0, y); c.lineTo(600, y); c.stroke(); }
      for (let xx = 0; xx < 600; xx += 60) { c.beginPath(); c.moveTo(xx, h - 150); c.lineTo(xx, h - 45); c.stroke(); }
      // The shelf and little café details sit outside the falling-bean corridor.
      c.fillStyle = '#aeb9bf'; c.fillRect(29, h - 168, 77, 7);
      c.fillStyle = '#8c9397'; c.beginPath(); c.roundRect(48, h - 211, 38, 43, [3, 3, 9, 9]); c.fill();
      c.strokeStyle = '#687883'; c.lineWidth = 3; c.beginPath(); c.moveTo(66, h - 209); c.quadraticCurveTo(58, h - 249, 77, h - 272); c.stroke();
      c.fillStyle = '#8c9b9f';
      for (let i = 0; i < 4; i++) { c.beginPath(); c.ellipse(64 + (i % 2 ? 10 : -7), h - 224 - i * 11, 12, 5, i % 2 ? -.7 : .7, 0, Math.PI * 2); c.fill(); }
      c.fillStyle = '#c4c8c9'; c.fillRect(0, h - 46, 600, 46);
      c.fillStyle = '#8d999f'; c.fillRect(0, h - 46, 600, 4);
      c.strokeStyle = '#b0b8bc'; c.beginPath(); c.moveTo(0, h - 17); c.lineTo(600, h - 17); c.stroke();
      if (this.game.state === 'idle') {
        this.bean(108, 82, 'coffee', -.45); this.bean(475, h - 161, 'gold', .5); this.bean(104, h - 96, 'coffee', .3);
      }
      // Paint the mug and its dark opening first, then let beans enter in front
      // of the back rim. The mouth mask hides their lower half inside the cup.
      this.mug(x, h - 100);
      for (const item of this.game.items) {
        if (item.y <= h - 100 && Math.abs(item.x - x) <= CUP_OPENING_HALF_WIDTH) {
          this.atMouth(() => this.bean(item.x, item.y, item.type, item.angle));
        } else this.bean(item.x, item.y, item.type, item.angle);
      }
      for (const effect of this.effects) {
        const pose = catchPose(this.game, effect);
        if (pose.visible) this.atMouth(() => this.bean(pose.x, pose.y, effect.type, pose.angle));
      }
      // The front edge stays in front of beans while they sink into the opening.
      c.strokeStyle = '#83939e'; c.lineWidth = 3;
      c.beginPath(); c.ellipse(x, h - 98, 52, 9, 0, 0, Math.PI); c.stroke();
      for (const effect of this.effects) {
        c.save(); c.globalAlpha = 1 - effect.age / .7;
        c.fillStyle = effect.type === 'decaf' ? '#a43826' : '#131e29';
        c.font = '600 21px "Instrument Sans", sans-serif'; c.textAlign = 'center';
        c.fillText(effect.type === 'decaf' ? '−1 life' : effect.type === 'gold' ? '+5' : '+1', effect.x, effect.y - 18 - (this.reducedMotion ? 0 : effect.age * 40));
        c.restore();
      }
    }
    atMouth(drawBean) {
      const c = this.ctx, { cupX, height } = this.game;
      c.save();
      c.beginPath();
      c.rect(0, 0, 600, height - 98);
      c.moveTo(cupX + CUP_OPENING_HALF_WIDTH, height - 98);
      c.ellipse(cupX, height - 98, CUP_OPENING_HALF_WIDTH, 6, 0, 0, Math.PI * 2);
      c.clip();
      drawBean();
      c.restore();
    }
    bean(x, y, type, angle) {
      const c = this.ctx;
      c.save(); c.translate(x, y); c.rotate(angle);
      const colors = type === 'gold' ? ['#ebc16f', '#b87b26'] : type === 'decaf' ? ['#dc7754', '#ad402e'] : ['#a16c4c', '#653b29'];
      const gradient = c.createLinearGradient(-12, -12, 12, 12); gradient.addColorStop(0, colors[0]); gradient.addColorStop(1, colors[1]);
      c.fillStyle = '#57422a18'; c.beginPath(); c.ellipse(2, 4, 12, 17, 0, 0, Math.PI * 2); c.fill();
      c.fillStyle = gradient; c.beginPath(); c.ellipse(0, 0, 12, 17, 0, 0, Math.PI * 2); c.fill();
      c.strokeStyle = type === 'coffee' ? '#e3b88a' : '#fff0c8'; c.lineWidth = 2;
      if (type === 'coffee') { c.beginPath(); c.moveTo(3, -13); c.bezierCurveTo(-9, -4, 9, 5, -4, 13); c.stroke(); }
      else { c.fillStyle = '#fff0c8'; c.font = type === 'gold' ? '19px serif' : '23px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(type === 'gold' ? '✦' : '×', 0, 1); }
      c.restore();
    }
    mug(x, y) {
      const c = this.ctx;
      c.fillStyle = '#131e2920'; c.beginPath(); c.ellipse(x + 6, y + 68, 76, 8, 0, 0, Math.PI * 2); c.fill();
      c.strokeStyle = '#22313d'; c.lineWidth = 11; c.beginPath(); c.ellipse(x + 56, y + 28, 19, 21, 0, 0, Math.PI * 2); c.stroke();
      const glaze = c.createLinearGradient(x - 54, y, x + 54, y); glaze.addColorStop(0, '#30414e'); glaze.addColorStop(.25, '#405565'); glaze.addColorStop(1, '#131e29');
      c.fillStyle = glaze; c.beginPath(); c.roundRect(x - 54, y, 108, 66, [4, 4, 24, 24]); c.fill();
      c.fillStyle = '#83939e'; c.beginPath(); c.ellipse(x, y + 2, 54, 10, 0, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#101923'; c.beginPath(); c.ellipse(x, y + 2, 47, 6, 0, 0, Math.PI * 2); c.fill();
      if (this.logo?.complete && this.logo.naturalWidth) {
        const width = 82, height = width * this.logo.naturalHeight / this.logo.naturalWidth;
        c.drawImage(this.logo, x - width / 2, y + 34 - height / 2, width, height);
      }
    }
  });
}
